import type { DeviceStatusEvent } from '@cobliteam/api-lib/src/dashboard/device/definitions';
import { FleetEventSourceRequesterV2 } from '@cobliteam/api-lib/src/dashboard/device/FleetEventSourceRequesterV2';
import type { UUID } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Environment } from '@cobliteam/react-environments';
import { useCallback, useEffect } from 'react';

import { QUERY_KEY, useSetQueryDataDevices } from '../useEntity/useDevices';
import useUser from '../useEntity/useUser';
import useFeatureFlags from '../useFeatureFlags';

const env = Environment.getInstance().getEnvironment();
const EVENTS_THRESHOLD = 100;

let eventsAmount = 0;
let activeFleetEventSourceV2: FleetEventSourceRequesterV2 | undefined;
let currentVehicleGroupIds = '';

function useSSEv2() {
  const { getSession } = useUser();
  const session = getSession();
  const { libraryExtensionEventSource, enableV2FleetEvents } = useFeatureFlags();
  const { devicesSlideSseTimeInMs: devicesSlideSseTimeSeconds } = useFeatureFlags();

  const setDevices = useSetQueryDataDevices(() => {
    eventsAmount += 1;
  });

  const setDevicesSlideSseTimeSeconds = useCallback((devicesSlideSseTimeSeconds: number) => {
    if (activeFleetEventSourceV2) {
      activeFleetEventSourceV2.setDevicesSlideSseTimeSeconds(devicesSlideSseTimeSeconds);
    }
  }, []);

  const subscribeToDeviceStatus = useCallback(
    (fleetEventSource: FleetEventSourceRequesterV2, fleetId: UUID, vehicleGroupIds?: UUID[]) => {
      const eventDataUpdated = (eventData: DeviceStatusEvent[]) => {
        const groupIds = vehicleGroupIds?.join(',');
        const queryKey = groupIds ? [QUERY_KEY, 'OBSERVATION_PERIOD_TRIGGER', groupIds] : [QUERY_KEY, 'OBSERVATION_PERIOD_TRIGGER'];

        setDevices({ queryKey, data: eventData });
      };

      return fleetEventSource.subscribeToDeviceStatus(fleetId, eventDataUpdated, vehicleGroupIds);
    },
    [setDevices]
  );

  const stopSSE = useCallback(() => {
    FleetEventSourceRequesterV2.setEnableV2FleetEvents(enableV2FleetEvents);
    if (activeFleetEventSourceV2) {
      activeFleetEventSourceV2.unsubscribe();
      activeFleetEventSourceV2 = undefined;
      currentVehicleGroupIds = '';
    }
  }, [enableV2FleetEvents]);

  const startSSE = useCallback(
    (vehicleGroupIds?: UUID[]) => {
      const groupIdsKey = vehicleGroupIds?.join(',') || '';

      if (activeFleetEventSourceV2 && currentVehicleGroupIds === groupIdsKey) {
        return;
      }

      if (activeFleetEventSourceV2) {
        stopSSE();
      }

      FleetEventSourceRequesterV2.setEnableV2FleetEvents(enableV2FleetEvents);

      const newFleetEventSource = new FleetEventSourceRequesterV2({
        host: env.apiHost,
        useEventSourceLibrary: libraryExtensionEventSource,
        devicesSlideSseTimeSeconds,
      });

      subscribeToDeviceStatus(newFleetEventSource, session.fleet.id, vehicleGroupIds).then(() => {
        activeFleetEventSourceV2 = newFleetEventSource;
        currentVehicleGroupIds = groupIdsKey;
      });
    },
    [enableV2FleetEvents, session.fleet.id, subscribeToDeviceStatus, stopSSE, libraryExtensionEventSource, devicesSlideSseTimeSeconds]
  );

  const restartSSE = useCallback(
    (vehicleGroupIds?: UUID[]) => {
      stopSSE();
      startSSE(vehicleGroupIds);
    },
    [startSSE, stopSSE]
  );

  useEffect(() => {
    return () => stopSSE();
  }, [stopSSE]);

  useEffect(() => {
    if (eventsAmount >= EVENTS_THRESHOLD) {
      DatadogTrack.action('Devices updated from SSE');
      eventsAmount = 0;
    }
  });

  return {
    startSSE,
    stopSSE,
    restartSSE,
    setDevicesSlideSseTimeSeconds,
  };
}

export default useSSEv2;
